// The rejection message the client receives, pre-filled in the Reject dialog
// from the affordability check's latest result: each reason it declined, in
// the client's terms with the figures and statement rows behind it, how the
// most they can repay was worked out, and the law (Microlending Act 7 of 2018
// and NAMFISA Standard ML.S.4) each rests on. Pure (rejectionMessage.test.js).
import { formatDateDMY } from '../utils/dateUtils';
import { toCurrency } from '../utils/adminMetrics';

export const monthLabel = (ym) => {
  const m = /^(\d{4})-(\d{2})$/.exec(String(ym || ''));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleString('en-GB', { month: 'long', year: 'numeric' }) : String(ym || '');
};

// Where the law says it, as the client is told.
export const LAW = {
  affordability: 'Microlending Act 7 of 2018, section 23(1)(b): a microlender may not grant a loan unless an assessment shows the borrower can afford to repay it.',
  assessment: 'NAMFISA Standard ML.S.4 (General Notice 74 of 2020), paragraph 3(1): the affordability assessment is made from your payslip, three months of bank statements, your credit bureau report, your debts and your living costs.',
  limit: 'NAMFISA Standard ML.S.4, paragraph 3(2): the monthly repayment may be at most 50% of what is left after your debts and living costs, and at most one third of your gross pay.',
  bureau: 'Microlending Act 7 of 2018, section 24(4): a credit bureau check must be made before a loan is granted.',
};

// The reasons the client is not told in detail (how their account was run,
// and Koniza's own policy): one plain line instead.
const OWN_POLICY = ['RETURNED_DEBIT_ORDERS', 'UNAUTHORISED_EXCESS', 'RISK_RATING_E'];
const OWN_POLICY_LINE = "Koniza's own lending policy, based on how your bank account was run over the last three months.";
const BUREAU_CODES = ['BUREAU_MISSING', 'BUREAU_STALE', 'BUREAU_ADVERSE'];
const AFFORDABILITY_CODES = ['NO_DISCRETIONARY_INCOME', 'INSTALMENT_ABOVE_STATUTORY'];

const rowLine = (t) => `${formatDateDMY(t.date)}  ${t.description || '-'}  ${toCurrency(t.amount)}`;

// One decline reason in the client's terms: { text, rows }.
const reasonFor = (r, { qualification, suggestion }) => {
  const rows = (r.transactions || []).slice(0, 6).map(rowLine);
  switch (r.code) {
    case 'NO_DISCRETIONARY_INCOME':
      return { text: 'After your debts and living costs are paid from your take-home pay, nothing is left over to repay a loan.' };
    case 'INSTALMENT_ABOVE_STATUTORY': {
      const asked = qualification?.proposed_instalment;
      const max = qualification?.statutory_max_instalment;
      const offer = suggestion?.amount > 0
        ? ` We can offer you up to ${toCurrency(suggestion.amount)} over ${suggestion.months} month${suggestion.months === 1 ? '' : 's'} instead.`
        : '';
      return {
        text: asked && max
          ? `The monthly repayment on the loan you asked for, ${toCurrency(asked)}, is more than the most the law allows you to repay, ${toCurrency(max)} a month.${offer}`
          : `The monthly repayment on the loan you asked for is more than the most the law allows you to repay.${offer}`,
      };
    }
    case 'SALARY_MISMATCH':
      return { text: 'The salary paid into your bank account in the latest month does not match the net pay on your payslip.', rows };
    case 'STATEMENT_MONTHS':
      return { text: `${String(r.detail || '').replace(/^The bank statements must/, 'Your bank statements must')} Please send statements for the full three months.` };
    case 'STATEMENT_EMPTY':
      return { text: 'We could not read the transactions on your bank statement. Please send a clear copy.' };
    case 'BUREAU_MISSING':
      return { text: 'We do not have your credit bureau report yet, and a loan cannot be granted without it.' };
    case 'BUREAU_STALE':
      return { text: 'Your credit report is older than 30 days. Please request a new one from TransUnion.' };
    case 'BUREAU_ADVERSE':
      return { text: 'Your credit report shows a default or court judgment that is still active.' };
    case 'TOO_MANY_MICROLOANS': {
      const n = /^(\d+)/.exec(String(r.title || ''))?.[1];
      return { text: `You already have ${n || 'three or more'} microloans running; we cannot add another while you have three or more.`, rows };
    }
    default:
      return OWN_POLICY.includes(r.code) ? { text: OWN_POLICY_LINE } : { text: r.title ? `${r.title}.` : null };
  }
};

// The older verdict's checks, for a result saved before the reasons were
// sent: the affordability test only.
const fromChecks = (summary) => (summary.checks || [])
  .filter((c) => !c.passed && c.key === 'affordable')
  .map(() => ({ code: 'NO_DISCRETIONARY_INCOME' }));

/**
 * The message, or null when the loan has no calculator result or the
 * calculator did not decline it (approved, a lower amount, or to check):
 * the reason is then typed.
 */
export const clientRejectionMessage = (calculator, loan) => {
  const summary = calculator?.summary;
  if (!calculator || !summary) return null;
  if (!(calculator.declined || summary.outcome === 'declined')) return null;
  const details = summary.details || {};
  const declines = Array.isArray(details.decline_reasons) && details.decline_reasons.length
    ? details.decline_reasons
    : fromChecks(summary);
  if (!declines.length && !details.affordability) return null;
  const qualification = details.qualification || null;
  const context = { qualification, suggestion: calculator.suggestion || null };

  const lines = ['Your loan application was not approved.'];

  // Each reason, once (the own-policy ones share one line).
  const seen = new Set();
  const reasons = declines.map((r) => reasonFor(r, context)).filter((x) => x?.text && !seen.has(x.text) && seen.add(x.text));
  if (reasons.length) {
    lines.push('', reasons.length === 1 ? 'The reason:' : 'The reasons:');
    for (const r of reasons) {
      lines.push(`- ${r.text}`);
      for (const row of r.rows || []) lines.push(`    ${row}`);
    }
  }

  // How the most they can repay was worked out, when affordability is a reason.
  const a = details.affordability;
  const codes = declines.map((r) => r.code);
  if (a && codes.some((c) => AFFORDABILITY_CODES.includes(c))) {
    const takenOff = a.rows.filter((r) => !r.added).reduce((s, r) => s + (Number(r.monthly) || 0), 0);
    const addedIn = a.rows.filter((r) => r.added).reduce((s, r) => s + (Number(r.monthly) || 0), 0);
    const left = Number(a.left_over) || 0;
    lines.push('', 'How we worked it out (a month):');
    lines.push(`- Your take-home pay (payslip): ${toCurrency(a.start)}`);
    if (addedIn > 0) lines.push(`- Plus other money paid into your account: ${toCurrency(addedIn)}`);
    if (takenOff > 0) lines.push(`- Less your debts, other monthly payments and living costs: ${toCurrency(takenOff)}`);
    lines.push(left > 0 ? `- Left over: ${toCurrency(left)}` : `- Left over: nothing (you are ${toCurrency(Math.abs(left))} short every month)`);
    if (left > 0 && qualification) {
      const third = qualification.cap_a !== null && qualification.cap_a !== undefined
        ? `one third of your gross pay of ${toCurrency(qualification.gross_pay)} (${toCurrency(qualification.cap_a)}) and `
        : '';
      lines.push(`- The most you can repay: ${toCurrency(qualification.statutory_max_instalment)} a month, the lower of ${third}half of what is left over (${toCurrency(qualification.cap_b)})`);
    }
  }

  // The law each reason rests on.
  lines.push('', 'The law we follow:');
  lines.push(`- ${LAW.affordability}`);
  lines.push(`- ${LAW.assessment}`);
  if (codes.some((c) => AFFORDABILITY_CODES.includes(c))) lines.push(`- ${LAW.limit}`);
  if (codes.some((c) => BUREAU_CODES.includes(c))) lines.push(`- ${LAW.bureau}`);

  lines.push('', 'You are welcome to apply again when your situation changes. If you think something was read wrongly, contact us and we will look at it again.');
  return lines.join('\n');
};
